import React, { useEffect } from "react";
import { usePohrebka2 } from "../../context/Pohrebka2Context";
import { getAllowedPrubehKeysForScenario, findSystemTemplateForOrderSelection } from "../../data/orderTemplates";

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 24,
  padding: 24,
  boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 16,
};

const labelStyle = {
  display: "block",
  fontSize: 14,
  fontWeight: 700,
  color: "#111827",
  marginBottom: 8,
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 14,
  border: "1px solid #d1d5db",
  fontSize: 15,
  background: "#fff",
  outline: "none",
  boxSizing: "border-box",
};

const textareaStyle = {
  ...inputStyle,
  minHeight: 120,
  resize: "vertical",
};

const DEATH_SCENARIO_OPTIONS = [
  { value: "", label: "Neurčeno" },
  { value: "nemocnice_ck", label: "Nemocnice ČK" },
  { value: "doma", label: "Úmrtí doma" },
  { value: "wagnerka", label: "Wagnerka" },
  { value: "mimo_ck", label: "Mimo ČK" },
];

function getPrubehOptionsForTyp(typZakazky) {
  const t = String(typZakazky || "").trim();

  if (t === "kremace") {
    return [
      { value: "", label: "Vyber další průběh" },
      { value: "obradni_sin_ck_nasledna_kremace", label: "Obřadní síň ČK / následná kremace" },
      { value: "kostel_nasledna_kremace", label: "Kostel / následná kremace" },
      { value: "cb_krematorium_rozlouceni", label: "Rozloučení v ČB krematoriu" },
    ];
  }

  if (t === "kremace_bez_obradu") {
    return [
      { value: "", label: "Vyber další průběh" },
      { value: "bez_obradu", label: "Bez obřadu" },
      { value: "bez_obradu_urna", label: "Bez obřadu + následné rozloučení s urnou" },
    ];
  }

  if (t === "pohreb") {
    return [
      { value: "", label: "Vyber další průběh" },
      { value: "obradni_sin_ck_hrob", label: "Obřadní síň ČK / pohřbení do hrobu" },
      { value: "kostel_hrob", label: "Kostel / pohřbení do hrobu" },
      { value: "bez_obradu_hrob", label: "Bez obřadu / pohřbení do hrobu" },
      { value: "prime_pohrbeni_hrob", label: "Přímé pohřbení do hrobu" },
    ];
  }

  if (t === "alternativa") {
    return [
      { value: "", label: "Vyber další průběh" },
      { value: "alternativa_standard", label: "Alternativní rozloučení" },
    ];
  }

  if (t === "obrad_s_urnou") {
    return [
      { value: "", label: "Vyber další průběh" },
      { value: "obrad_s_urnou_standard", label: "Obřad s urnou" },
    ];
  }

  return [{ value: "", label: "Bez výběru" }];
}



export default function Pohrebka2ZakladForm() {
  const { order, setOrder, activeOrderId } = usePohrebka2();
  const zaklad = order?.zaklad || {};
  const prubehOptions = getPrubehOptionsForTyp(zaklad.typZakazky || "");
  const allowedPrubehKeys = getAllowedPrubehKeysForScenario(
    zaklad.typZakazky || "",
    zaklad.scenarUmrti || ""
  );
  const filteredPrubehOptions = prubehOptions.filter((item) =>
    allowedPrubehKeys.includes(String(item?.value || "").trim())
  );

  useEffect(() => {
    const typZakazky = String(zaklad?.typZakazky || "").trim();
    const scenarUmrti = String(zaklad?.scenarUmrti || "").trim();
    const prubehKey = String(zaklad?.prubehKey || "").trim();

    if (!typZakazky || !prubehKey) return;

    const matched = findSystemTemplateForOrderSelection({
      typZakazky,
      scenarUmrti,
      prubehKey,
    });

    const matchedId = String(matched?.id || "").trim();
    const matchedName = String(matched?.name || "").trim();

    const currentId = String(zaklad?.selectedTemplateId || order?.meta?.selectedTemplateId || "").trim();
    const currentName = String(order?.meta?.selectedTemplateName || "").trim();

    if (matchedId) {
      if (currentId === matchedId && currentName === matchedName) return;

      setOrder((prev) => ({
        ...(prev || {}),
        zaklad: {
          ...(prev?.zaklad || {}),
          selectedTemplateId: matchedId,
          prubehKey,
          scenarUmrti,
        },
        meta: {
          ...(prev?.meta || {}),
          selectedTemplateId: matchedId,
          selectedTemplateName: matchedName,
        },
        prevoz: {
          ...(prev?.prevoz || {}),
          ...(scenarUmrti === "wagnerka" ? { coolingScenario: "wagnerka" } : {}),
        },
      }));
      return;
    }

    if (!currentId && !currentName) return;

    setOrder((prev) => ({
      ...(prev || {}),
      zaklad: {
        ...(prev?.zaklad || {}),
        prubehKey,
        scenarUmrti,
        selectedTemplateId: "",
      },
      meta: {
        ...(prev?.meta || {}),
        selectedTemplateId: "",
        selectedTemplateName: "",
      },
    }));
  }, [
    zaklad?.typZakazky,
    zaklad?.scenarUmrti,
    zaklad?.prubehKey,
    zaklad?.selectedTemplateId,
    order?.meta?.selectedTemplateId,
    order?.meta?.selectedTemplateName,
    setOrder,
  ]); // AUTO_TEMPLATE_MATCH_ZAKLAD

  useEffect(() => {
    const options = Array.isArray(filteredPrubehOptions) ? filteredPrubehOptions : [];
    const realOptions = options.filter((item) => String(item?.value || "").trim());

    if (realOptions.length !== 1) return;

    const onlyValue = String(realOptions[0]?.value || "").trim();
    const currentValue = String(zaklad?.prubehKey || "").trim();

    if (!onlyValue || currentValue === onlyValue) return;

    setOrder((prev) => ({
      ...(prev || {}),
      zaklad: {
        ...(prev?.zaklad || {}),
        prubehKey: onlyValue,
      },
    }));
  }, [filteredPrubehOptions, zaklad?.prubehKey, setOrder]); // AUTO_PRUBEH_ZAKLAD

  useEffect(() => {
    const handler = (e) => {
      const date = String(e?.detail?.date || "").slice(0, 10);
      const time = String(e?.detail?.time || "").slice(0, 5);
      const poznamka = String(e?.detail?.poznamka || "").trim();
      const typZakazky = String(e?.detail?.typZakazky || "").trim();
      if (!date) return;

      setOrder((prev) => {
        const base = prev || {};
        return {
          ...base,
          zaklad: {
            ...(base.zaklad || {}),
            typZakazky: base?.zaklad?.typZakazky || typZakazky,
            ...(poznamka ? { poznamka } : {}),
          },
            obrad: {
            ...(base.obrad || {}),
            datum: base?.obrad?.datum || date,
            cas: base?.obrad?.cas || time,
          },
        };
      });
    };

    window.addEventListener("pohrebka2-calendar-create-date", handler);
    return () => window.removeEventListener("pohrebka2-calendar-create-date", handler);
  }, [setOrder]);

  const setField = (name, value) => {
    const val =
      name === "navazujiciObradSUrnou"
        ? !!value
        : String(value || "");

    setOrder((prev) => {
      const base = prev || {};
      const next = {
        ...base,
        zaklad: {
          ...(base.zaklad || {}),
          [name]: val,
        },
      };

      if (name === "typZakazky") {
        const currentObrad = base?.obrad || {};
        const currentPrevoz = base?.prevoz || {};
        let obradTyp = currentObrad.typ || "";

        if (val !== "kremace_bez_obradu") {
          next.zaklad = {
            ...(next.zaklad || {}),
            navazujiciObradSUrnou: false,
          };
        }

        if (val === "kremace") obradTyp = "kremace";
        else if (val === "kremace_bez_obradu") obradTyp = "kremace_bez_obradu";
        else if (val === "pohreb") obradTyp = "pohreb";
        else if (val === "alternativa") obradTyp = "alternativa";
        else if (val === "obrad_s_urnou") obradTyp = "obrad_s_urnou";
        else if (val === "prevoz") obradTyp = "samostatny_prevoz";
        else if (val === "ostatni") obradTyp = "ostatni";
        else if (!val) obradTyp = "";

        const cleanedObrad = {
          ...currentObrad,
          typ: obradTyp,
        };

        const cleanedPrevoz = {
          ...currentPrevoz,
        };

        const isCeremonyType = ["kremace", "pohreb", "alternativa", "obrad_s_urnou"].includes(val);
        const isKremaceType = ["kremace", "kremace_bez_obradu"].includes(val);
        const isPohrebType = val === "pohreb";
        const isPrevozType = val === "prevoz";
        const isKremaceBezObraduType = val === "kremace_bez_obradu";

        if (!isKremaceType) {
          cleanedObrad.krematorium = "";
          cleanedObrad.krematoriumAdresa = "";
        }

        if (!isPohrebType) {
          cleanedObrad.hrbitov = "";
          cleanedObrad.hrbitovAdresa = "";
        }

        if (!isCeremonyType) {
          cleanedObrad.datum = "";
          cleanedObrad.cas = "";
          cleanedObrad.misto = "";
          cleanedObrad.mistoAdresa = "";
          cleanedObrad.mistoId = "";
          cleanedObrad.mistoSkupina = "";
          cleanedObrad.ceremonyVariant = "";
          cleanedObrad.churchServiceType = "";
          cleanedObrad.fararJmeno = "";
          cleanedObrad.recnik = "";
          cleanedObrad.recnikTyp = "";
          cleanedObrad.recnikJmeno = "";
          cleanedObrad.hudba = "";
          cleanedObrad.hudbaTyp = "";
          cleanedObrad.hudbaZajistuje = "";
          cleanedObrad.hudbaZajistujeJmeno = "";
          cleanedObrad.roleAssignments = [];
          cleanedObrad.vehicleAssignments = [];
        }

        if (isKremaceBezObraduType) {
          cleanedObrad.datum = "";
          cleanedObrad.cas = "";
          cleanedObrad.misto = "";
          cleanedObrad.mistoAdresa = "";
          cleanedObrad.mistoId = "";
          cleanedObrad.mistoSkupina = "";
          cleanedObrad.ceremonyVariant = "";
          cleanedObrad.churchServiceType = "";
          cleanedObrad.fararJmeno = "";
          cleanedObrad.hrbitov = "";
          cleanedObrad.hrbitovAdresa = "";
          cleanedObrad.recnik = "";
          cleanedObrad.recnikTyp = "";
          cleanedObrad.recnikJmeno = "";
          cleanedObrad.hudba = "";
          cleanedObrad.hudbaTyp = "";
          cleanedObrad.hudbaZajistuje = "";
          cleanedObrad.hudbaZajistujeJmeno = "";
          cleanedObrad.roleAssignments = [];
          cleanedObrad.vehicleAssignments = [];
        }

        if (isPrevozType) {
          cleanedPrevoz.roleAssignments = Array.isArray(currentPrevoz?.roleAssignments)
            ? currentPrevoz.roleAssignments
            : [];
          cleanedPrevoz.vehicleAssignments = Array.isArray(currentPrevoz?.vehicleAssignments)
            ? currentPrevoz.vehicleAssignments
            : [];
        }

        if (!isPrevozType) {
          cleanedPrevoz.roleAssignments = Array.isArray(currentPrevoz?.roleAssignments)
            ? currentPrevoz.roleAssignments
            : [];
          cleanedPrevoz.vehicleAssignments = Array.isArray(currentPrevoz?.vehicleAssignments)
            ? currentPrevoz.vehicleAssignments
            : [];
        }

        next.obrad = cleanedObrad;
        next.prevoz = cleanedPrevoz;
      }

      if (name === "zesnuly") {
        next.vstupniInformace = {
          ...(base.vstupniInformace || {}),
        };
        next.zesnulaOsoba = {
          ...(base.zesnulaOsoba || {}),
        };

        const full = val.trim();
        const parts = full.split(/\s+/).filter(Boolean);
        const first = parts[0] || "";
        const last = parts.length > 1 ? parts.slice(1).join(" ") : "";

        if (full) {
          next.vstupniInformace.jmenoZesnuleho = first;
          next.vstupniInformace.prijmeniZesnuleho = last;
          next.zesnulaOsoba.jmeno = first;
          next.zesnulaOsoba.prijmeni = last;
        }
      }

      if (name === "vypravitel") {
        next.vypravitel = {
          ...(base.vypravitel || {}),
        };

        const full = val.trim();
        const parts = full.split(/\s+/).filter(Boolean);
        const first = parts[0] || "";
        const last = parts.length > 1 ? parts.slice(1).join(" ") : "";

        next.vypravitel.jmeno = first;
        next.vypravitel.prijmeni = last;
      }

      return next;
    });
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 34, fontWeight: 900, color: "#111827", lineHeight: 1.05 }}>Základ</div>
          <div style={{ marginTop: 8, color: "#6b7280", fontSize: 16 }}>
            První čistý formulář nové větve Pohřebka 2.
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 14, color: "#6b7280", fontWeight: 700 }}>Aktivní zakázka</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: "#111827" }}>{activeOrderId || "-"}</div>
        </div>
      </div>

      <div style={gridStyle}>
        <div>
          <label style={labelStyle}>Typ zakázky</label>
          <select
            value={zaklad.typZakazky || ""}
            onChange={(e) => setField("typZakazky", e.target.value)}
            style={inputStyle}
          >
            <option value="">Vyber typ</option>
            <option value="kremace">Kremace s obřadem</option>
            <option value="kremace_bez_obradu">Kremace bez obřadu</option>
            <option value="pohreb">Pohřeb</option>
            <option value="alternativa">Alternativní rozloučení</option>
            <option value="obrad_s_urnou">Obřad s urnou</option>
            <option value="prevoz">Pouze převoz</option>
            <option value="ostatni">Ostatní</option>
          </select>
        </div>

        <div>
          <label style={labelStyle}>Místo / scénář úmrtí</label>
          <select
            value={String(order?.zaklad?.scenarUmrti || "")}
            onChange={(e) => {
              const value = String(e.target.value || "");
              setOrder((prev) => ({
                ...(prev || {}),
                zaklad: {
                  ...(prev?.zaklad || {}),
                  scenarUmrti: value,
                },
                prevoz: {
                  ...(prev?.prevoz || {}),
                  coolingScenario: value === "wagnerka" ? "wagnerka" : "",
                },
              }));
            }}
            style={inputStyle}
          >
            {DEATH_SCENARIO_OPTIONS.map((item) => (
              <option key={item.value || "neurceno"} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Další průběh</label>
          <select
            value={String(order?.zaklad?.prubehKey || "")}
            disabled={!String(zaklad?.typZakazky || "").trim() || filteredPrubehOptions.length <= 1}
            onChange={(e) =>
              setOrder((prev) => ({
                ...(prev || {}),
                zaklad: {
                  ...(prev?.zaklad || {}),
                  prubehKey: String(e.target.value || ""),
                },
              }))
            }
            style={{
              ...inputStyle,
              opacity: !String(zaklad?.typZakazky || "").trim() || filteredPrubehOptions.length <= 1 ? 0.7 : 1,
              cursor: !String(zaklad?.typZakazky || "").trim() || filteredPrubehOptions.length <= 1 ? "not-allowed" : "pointer",
              background:
                !String(zaklad?.typZakazky || "").trim() || filteredPrubehOptions.length <= 1
                  ? "#f3f4f6"
                  : "#fff",
            }}
          >
            {!String(zaklad?.typZakazky || "").trim() ? (
              <option value="">Nejdřív vyber typ zakázky</option>
            ) : filteredPrubehOptions.length <= 1 ? (
              <option value={String(filteredPrubehOptions[0]?.value || "")}>
                {String(filteredPrubehOptions[0]?.label || "Průběh se doplní automaticky")}
              </option>
            ) : (
              filteredPrubehOptions.map((item) => (
                <option key={item.value || "empty"} value={item.value}>
                  {item.label}
                </option>
              ))
            )}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Datum sjednání</label>
          <input
            type="date"
            value={zaklad.datumSjednani || ""}
            onChange={(e) => setField("datumSjednani", e.target.value)}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Zesnulý</label>
          <input
            type="text"
            value={zaklad.zesnuly || ""}
            onChange={(e) => setField("zesnuly", e.target.value)}
            placeholder="Jméno a příjmení zesnulého"
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Vypravitel</label>
          <input
            type="text"
            value={zaklad.vypravitel || ""}
            onChange={(e) => setField("vypravitel", e.target.value)}
            placeholder="Jméno vypravitele"
            style={inputStyle}
          />
        </div>
      </div>

      {zaklad.typZakazky === "kremace_bez_obradu" ? (
        <div style={{ marginTop: 18 }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 15,
              fontWeight: 700,
              color: "#111827",
            }}
          >
            <input
              type="checkbox"
              checked={!!zaklad.navazujiciObradSUrnou}
              onChange={(e) => setField("navazujiciObradSUrnou", e.target.checked)}
            />
            Následné rozloučení s urnou
          </label>
          <div style={{ marginTop: 8, fontSize: 13, color: "#6b7280" }}>
            Po zaškrtnutí se ve službách zpřístupní i urnová a obřadní část v rámci jedné zakázky po kremaci bez obřadu.
          </div>
        </div>
      ) : null}

      <div style={{ marginTop: 18, fontSize: 13, color: "#6b7280" }}>
        Aktivní scénář chlazení / mražení:{" "}
        <strong>{String(order?.prevoz?.coolingScenario || "") === "wagnerka" ? "Úmrtí Wagnerka" : "Standard"}</strong>
      </div>

      <div style={{ marginTop: 18 }}>
        <label style={labelStyle}>Poznámka</label>
        <textarea
          value={zaklad.poznamka || ""}
          onChange={(e) => setField("poznamka", e.target.value)}
          placeholder="Interní poznámka k zakázce"
          style={textareaStyle}
        />
      </div>
    </div>
  );
}
